import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import { format, parseISO } from "date-fns";

import * as actions from "../../store/actions/index";
import { formatCurrency, withRouter } from "../../shared/utility";
import EmptyModal from "../../components/modals/EmptyModal";
import ActionLink from "../../components/controls/ActionLink";
import ActionButton from "../../components/controls/ActionButton";
import Button from "../../components/ui/Button";
import PurchaseOrderStatusBadge from "../../components/forms/purchaseOrders/PurchaseOrderStatusBadge";
import { usePurchaseOrdersViewModel } from "../../viewmodels/purchaseOrders/usePurchaseOrdersViewModel";
import {
  canDeletePurchaseOrder,
  canReceivePurchaseOrder,
} from "../../components/forms/purchaseOrders/PurchaseOrderStatusBadge";
import DateRangeFilter from "../../components/filterDropdown/DateRangeFilter";
import { formatDateRangeSummary } from "../../shared/filterUtils";
import AuditHistoriesLink from "../../components/audit/AuditHistoriesLink";
import OperationalTableToolbar from "../../components/operational/OperationalTableToolbar";
import OperationalDataTableSkeleton from "../../components/operational/OperationalDataTableSkeleton";

import { Paginator } from "primereact/paginator";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

const SEARCH_DEBOUNCE_MS = 400;

function formatPoDate(raw: unknown) {
  if (!raw) return "—";
  try {
    return format(parseISO(String(raw).slice(0, 10)), "MMM d, yyyy");
  } catch {
    return String(raw);
  }
}

function PurchaseOrders(props: any) {
  const { logout, addSnackbar } = props;
  const vm = usePurchaseOrdersViewModel(logout, addSnackbar);
  const {
    filters,
    setFilters,
    showDates,
    setShowDates,
    statusFilter,
    getPurchaseOrders,
    resetFilters,
  } = vm;

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [showFilters, setShowFilters] = useState(false);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getSearchValue = () => searchRef.current?.value?.trim() || "";

  useEffect(() => {
    getPurchaseOrders("", 1, "");
  }, []);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleSearch = (
    filterOverride = filters,
    statusOverride = statusFilter,
  ) => {
    setFirst(0);
    getPurchaseOrders(getSearchValue(), 1, statusOverride, filterOverride);
  };

  const scheduleSearch = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      handleSearch();
    }, SEARCH_DEBOUNCE_MS);
  };

  const handlePagination = (event: any) => {
    const currentPage = event.page + 1;
    setFirst(event.first);
    setRows(event.rows);
    getPurchaseOrders(getSearchValue(), currentPage, statusFilter, filters);
  };

  const applyDateFilter = (startDate: any, endDate: any) => {
    const nextFilters = {
      ...filters,
      startDate: startDate !== undefined ? startDate : filters.startDate,
      endDate: endDate !== undefined ? endDate : filters.endDate,
    };
    setFilters(nextFilters);
    handleSearch(nextFilters);
  };

  const clearDates = () => {
    const nextFilters = {
      ...filters,
      startDate: null,
      endDate: null,
    };
    setFilters(nextFilters);
    handleSearch(nextFilters);
  };

  const applyStatusFilter = (nextStatus: string) => {
    handleSearch(filters, nextStatus);
  };

  const handleClearAll = () => {
    if (searchRef.current) {
      searchRef.current.value = "";
    }
    setShowFilters(false);
    setFirst(0);
    resetFilters();
  };

  const totalRecords = vm.purchaseOrders?.total ?? 0;
  const hasDateFilter = Boolean(filters.startDate && filters.endDate);
  const rangeSummary = formatDateRangeSummary(
    filters.startDate,
    filters.endDate,
  );
  const hasSearch = Boolean(vm.search);
  const activeFilterCount = statusFilter ? 1 : 0;
  const hasAnyFilters = hasDateFilter || activeFilterCount > 0 || hasSearch;
  const resultMeta = `${totalRecords.toLocaleString()} result${totalRecords === 1 ? "" : "s"}`;

  const poNumberBody = (row: any) => row.po_number ?? `PO-${row.id}`;
  const supplierBody = (row: any) =>
    row.supplier_name ?? row.supplier?.name ?? "—";
  const expectedDeliveryBody = (row: any) =>
    formatPoDate(row.expected_delivery_date);
  const statusBody = (row: any) => (
    <PurchaseOrderStatusBadge status={row.status} />
  );
  const orderedTotalBody = (row: any) => {
    if (row.total_cost != null) return formatCurrency(row.total_cost);
    if (row.ordered_total != null) return formatCurrency(row.ordered_total);
    return "—";
  };
  const receivedBody = (row: any) =>
    `${row.received_amount ?? "0"} / ${row.ordered_amount ?? "0"}`;
  const actionsBody = (row: any) => {
    const id = row.id;
    return (
      <div className="flex flex-wrap items-center gap-2">
        <ActionLink to={`/purchase-orders/${id}`} variant="primary">
          Edit
        </ActionLink>
        {canReceivePurchaseOrder(row.status) && (
          <ActionLink
            to={`/purchase-orders/${id}/receive`}
            variant={"info" as any}
          >
            Receive
          </ActionLink>
        )}
        {canDeletePurchaseOrder(row.status, row.received_amount) && (
          <ActionButton
            color="red"
            onClick={() => vm.askDeletePurchaseOrder(row)}
          >
            Delete
          </ActionButton>
        )}
      </div>
    );
  };

  const emptyMessage = (
    <div className="px-6 py-12 text-center">
      <p className="text-sm text-[#64748B]">
        No purchase orders match the current filters.
      </p>
      {hasAnyFilters ? (
        <Button
          type="button"
          variant="soft"
          size="md"
          className="mt-4"
          onClick={handleClearAll}
        >
          Clear filters
        </Button>
      ) : null}
    </div>
  );

  const tableSkeletonColumns = [
    "PO #",
    "Supplier",
    "Expected delivery",
    "Status",
    "Ordered total",
    "Received",
    "Actions",
  ];

  return (
    <div className="qatrace-screen qatrace-module-page">
      <div>
        <div className="space-y-5 px-4 sm:px-6 lg:px-8">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <nav aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-2 text-xs font-medium sm:text-sm">
                <li>
                  <Link
                    to="/purchase-orders/new"
                    className="text-[#16A34A] transition hover:text-[#15803D]"
                  >
                    Purchasing
                  </Link>
                </li>
                <li className="text-[#CBD5E1]" aria-hidden="true">
                  /
                </li>
                <li className="text-[#475569]">Purchase Orders</li>
              </ol>
            </nav>

            <div className="flex flex-wrap items-center gap-2">
              <AuditHistoriesLink slug="purchase-order" />
              <Link
                to="/purchase-orders/new"
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-[#16A34A] bg-[#16A34A] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:border-[#15803D] hover:bg-[#15803D] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#16A34A]"
              >
                <span className="text-lg leading-none">+</span>
                Create purchase order
              </Link>
            </div>
          </header>

          <div className="overflow-visible rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <OperationalTableToolbar
              className=""
              searchRowClassName=""
              searchRow={
                <>
                  <MagnifyingGlassIcon
                    className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#94A3B8]"
                    aria-hidden="true"
                  />
                  <input
                    type="search"
                    name="searchPurchaseOrders"
                    id="searchPurchaseOrders"
                    className="block min-h-10 w-full rounded-xl border border-[#E2E8F0] bg-white py-2 pl-10 pr-3 text-sm text-[#0F172A] shadow-sm placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:outline-none focus:ring-2 focus:ring-[#BBF7D0]"
                    placeholder="Search PO # or supplier"
                    ref={searchRef}
                    onChange={scheduleSearch}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (searchDebounceRef.current) {
                          clearTimeout(searchDebounceRef.current);
                        }
                        handleSearch();
                      }
                    }}
                  />
                </>
              }
              filterActions={
                <>
                  <DateRangeFilter
                    onChange={(startDate: any, endDate: any) =>
                      setFilters({
                        ...filters,
                        startDate,
                        endDate,
                      })
                    }
                    onClear={clearDates}
                    endDate={filters.endDate}
                    startDate={filters.startDate}
                    setShowDates={setShowDates}
                    showDates={showDates}
                    applyChanges={applyDateFilter}
                    maxDate={new Date()}
                    rangeSummary={hasDateFilter ? rangeSummary : undefined}
                  />
                  <Button
                    type="button"
                    variant={showFilters ? "soft" : "secondary"}
                    size="md"
                    onClick={() => setShowFilters(!showFilters)}
                    aria-expanded={showFilters}
                  >
                    <AdjustmentsHorizontalIcon
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                    {showFilters ? "Hide filters" : "Filters"}
                    {activeFilterCount > 0 ? (
                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#16A34A] px-1.5 text-xs font-bold leading-none text-white">
                        {activeFilterCount}
                      </span>
                    ) : null}
                  </Button>
                </>
              }
              footer={
                showFilters ? (
                  <div className="flex min-w-[9rem] flex-col gap-1">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
                      Status
                    </span>
                    <select
                      className="h-10 w-[min(13rem,100%)] rounded-xl border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A]"
                      value={statusFilter}
                      onChange={(e) => applyStatusFilter(e.target.value)}
                    >
                      <option value="">All statuses</option>
                      {vm.statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null
              }
              trailing={
                <>
                  <span className="text-xs font-medium text-[#64748B]">
                    {resultMeta}
                  </span>
                  {hasAnyFilters ? (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      onClick={handleClearAll}
                      className="!min-h-0 text-xs"
                    >
                      Clear all
                    </Button>
                  ) : null}
                </>
              }
            />

            <div className="operational-embedded-table overflow-hidden">
              {vm.loading ? (
                <OperationalDataTableSkeleton
                  columns={tableSkeletonColumns}
                  rows={10}
                />
              ) : (
                <DataTable
                  value={vm.purchaseOrders?.data ?? []}
                  dataKey="id"
                  className="p-datatable-sm text-sm"
                  emptyMessage={emptyMessage}
                >
                  <Column
                    header="PO #"
                    body={poNumberBody}
                    style={{ minWidth: "8rem" }}
                    bodyClassName="font-semibold text-[#0F172A]"
                  />
                  <Column
                    header="Supplier"
                    body={supplierBody}
                    style={{ minWidth: "12rem" }}
                  />
                  <Column
                    header="Expected delivery"
                    body={expectedDeliveryBody}
                    style={{ minWidth: "10rem" }}
                  />
                  <Column
                    header="Status"
                    body={statusBody}
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    header="Ordered total"
                    body={orderedTotalBody}
                    style={{ minWidth: "8rem" }}
                    bodyClassName="tabular-nums"
                  />
                  <Column
                    header="Received"
                    body={receivedBody}
                    style={{ minWidth: "8rem" }}
                    bodyClassName="tabular-nums"
                  />
                  <Column
                    header="Actions"
                    body={actionsBody}
                    style={{ minWidth: "11rem" }}
                  />
                </DataTable>
              )}
            </div>

            <div className="border-t border-[#E2E8F0] px-4 py-4 sm:px-5">
              <Paginator
                className="operational-embedded-paginator"
                first={first}
                rows={rows}
                totalRecords={totalRecords}
                onPageChange={handlePagination}
              />
            </div>
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={vm.emptyModal.childComponent}
        open={vm.emptyModal.open}
        setOpen={vm.setEmptyModelOpen}
      />
    </div>
  );
}

const mapDispatchToProps = (dispatch: any) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar: any) => dispatch(actions.addSnackbar(snackbar)),
});

export default withRouter(connect(null, mapDispatchToProps)(PurchaseOrders));
